import { useRef, useState } from 'react'

import { formatBytes } from '../../lib/format'
import { Banner, Button } from '../../ui'
import type { ResumableUpload } from '../../upload/types'

interface Props {
  resumable: ResumableUpload[]
  onResume: (target: ResumableUpload, file: File) => void
}

/**
 * Subidas que quedaron a medias.
 *
 * El navegador no conserva el `File` entre recargas —por seguridad, y no hay
 * forma de evitarlo—, así que el usuario tiene que volver a elegir el mismo
 * archivo. Lo que sí se conserva son los ETags de las partes ya subidas, de
 * modo que continúa desde donde se quedó en lugar de empezar de cero.
 *
 * En el mercado peruano esto no es un caso límite: una boda de 400 RAW puede
 * tardar más de dos horas, y en móvil el navegador suspende la pestaña.
 */
export function ResumablePanel({ resumable, onResume }: Props) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [target, setTarget] = useState<ResumableUpload | null>(null)

  if (resumable.length === 0) return null

  return (
    <Banner tone="warning" title="Tienes subidas sin terminar">
      <p>Vuelve a elegir el mismo archivo y continuará desde donde se quedó.</p>

      <input
        ref={inputRef}
        type="file"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0]
          if (file && target) onResume(target, file)
          event.target.value = ''
          setTarget(null)
        }}
      />

      <ul className="mt-3 flex flex-col gap-2">
        {resumable.map((entry) => (
          <li key={entry.sessionId} className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate text-label font-medium text-ink">{entry.filename}</p>
              <p className="text-caption text-ink-subtle" data-numeric>
                {entry.completedParts}/{entry.totalParts} partes · {formatBytes(entry.sizeBytes)}
              </p>
            </div>

            <Button
              size="sm"
              onClick={() => {
                setTarget(entry)
                inputRef.current?.click()
              }}
            >
              Continuar
            </Button>
          </li>
        ))}
      </ul>
    </Banner>
  )
}
